import { Select as SelectAnt, type SelectProps as SelectPropsAnt } from 'antd';
import { useMemo } from 'react';

import './Select.scss';

import Colors from 'src/common/Colors';
import { DEFAULT_FIELD_STYLES } from 'src/components/base/controls/constants';
import { cNs } from 'src/helpers';
import type { IControl, ISelectOption } from 'src/types';

const DEFAULT_PLACEHOLDER = 'Select';

const commonStyles = {
  ...DEFAULT_FIELD_STYLES,
};

function filterOptionHandler<T>(input: string, option?: ISelectOption<T>): boolean {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
}

export type SelectProps<T> = IControl<T> &
  Omit<
    SelectPropsAnt<unknown, ISelectOption<unknown>>,
    'onChange' | 'placeholder' | 'options' | 'onSelect' | 'onDeselect' | 'filterSort'
  > & {
    options: ISelectOption<T>[];
    placeholder?: string;

    description?: string | null;
  };

export function Select({
  label,
  placeholder,
  mode,
  status,
  disabled,
  value,
  controlStyle,
  controlClassName,
  description,
  ...rest
}: SelectProps<unknown>) {
  const style = useMemo(() => {
    const volatileStyles = {
      borderColor: status === 'error' ? Colors.dangerMedium : Colors.border,
      backgroundColor: disabled ? Colors.backgroundDisabled : Colors.white,
    };

    return {
      ...commonStyles,
      ...volatileStyles,
    };
  }, [status, disabled]);

  const valueWrapper = useMemo(
    () => (value === null ? null : Array.isArray(value) || typeof value === 'string' ? value : undefined),
    [value]
  );

  return (
    <div className={cNs('select__container', controlClassName)} style={controlStyle}>
      {label ? typeof label !== 'object' ? <div className="select__title">{label}</div> : label : null}
      {description ? <div className="select__description">{description}</div> : null}
      <SelectAnt<unknown, ISelectOption<unknown>>
        showSearch={true}
        {...rest}
        value={valueWrapper}
        placeholder={placeholder ?? DEFAULT_PLACEHOLDER}
        status={status}
        mode={mode}
        disabled={disabled}
        filterOption={filterOptionHandler}
        dropdownStyle={{ ...DEFAULT_FIELD_STYLES }}
        style={style}
      />
    </div>
  );
}
